import { type FC, useCallback, useState } from "react";
import { useFetch } from "@money/frontend/components/hooks/use.fetch.tsx";
import useSWR from "swr";
import { useModals } from "@money/frontend/store/store";
import { Input } from "@money/frontend/components/ui/input";
import { Button } from "@money/frontend/components/ui/button";
import { useRouter } from "next/navigation";

interface Provider {
  identifier: string;
  title: string;
  type: "oauth2" | "apiKey";
}

const ApiKeyModal: FC<{
  provider: Provider;
  close: () => void;
  onSubmit: (apiKey: string) => void;
}> = ({ provider, close, onSubmit }) => {
  const [apiKey, setApiKey] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!apiKey.trim()) return;
    setLoading(true);
    await onSubmit(apiKey);
    setLoading(false);
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-secondary">
        Enter your {provider.title} API key to connect your account.
      </p>
      <Input
        type="text"
        placeholder="Enter API key"
        value={apiKey}
        onChange={(e) => setApiKey(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleSubmit();
          }
        }}
      />
      <div className="flex gap-3">
        <Button onClick={handleSubmit} disabled={!apiKey.trim() || loading}>
          {loading ? "Connecting..." : "Connect"}
        </Button>
        <Button variant="outline" onClick={close} disabled={loading}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

export const ProviderComponent: FC<{ close: () => void }> = () => {
  const fetch = useFetch();
  const modals = useModals();
  const router = useRouter();
  const [loading, setLoading] = useState<string | null>(null);

  const loadProviders = useCallback(async () => {
    return await (await fetch("/billing")).json();
  }, []);

  const { data } = useSWR<{ list: Provider[] }>("/billing", loadProviders);

  const handleOAuth2Provider = async (provider: Provider) => {
    try {
      setLoading(provider.identifier);

      // Make GET request to get the OAuth URL
      const response = await fetch(`/billing/${provider.identifier}/url`);
      const result = await response.json();

      if (result?.url) {
        // Redirect to the OAuth URL
        window.location.href = result.url;
      }
    } catch (error) {
      console.error("Failed to connect provider:", error);
      setLoading(null);
    }
  };

  const handleApiKeyProvider = async (provider: Provider) => {
    modals.show({
      label: `Connect ${provider.title}`,
      component: (close) => (
        <ApiKeyModal
          provider={provider}
          close={close}
          onSubmit={async (apiKey: string) => {
            try {
              setLoading(provider.identifier);

              // Make POST request with API key
              const response = await fetch(
                `/billing/${
                  provider.identifier
                }/url?apiKey=${encodeURIComponent(apiKey)}`
              );
              const result = await response.json();

              if (result.url === "false") {
                console.log("Invalid API key provided");
                setLoading(null);
              } else {
                // Close modal and redirect to billing connect page
                modals.closeAll();
                router.push(`/billing/connect?state=${result.url}`);
              }
            } catch (error) {
              console.error("Failed to connect provider:", error);
              setLoading(null);
            }
          }}
        />
      ),
    });
  };

  const handleProviderClick = async (provider: Provider) => {
    if (provider.type === "oauth2") {
      await handleOAuth2Provider(provider);
    } else if (provider.type === "apiKey") {
      await handleApiKeyProvider(provider);
    }
  };

  if (!data?.list) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-secondary">Loading providers...</div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="grid grid-cols-2 gap-4">
        {data.list.map((provider) => (
          <button
            key={provider.identifier}
            onClick={() => handleProviderClick(provider)}
            disabled={loading === provider.identifier}
            className="flex flex-col items-center gap-4 p-6 rounded-xl border-2 border-input-border hover:border-btn-primary hover:bg-boxHover transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="w-16 h-16 rounded-lg overflow-hidden bg-background flex items-center justify-center">
              <img
                src={`/providers/${provider.identifier}.png`}
                alt={provider.title}
                className="w-full h-full object-contain"
                onError={(e) => {
                  // Fallback if image doesn't exist
                  const target = e.target as HTMLImageElement;
                  target.style.display = "none";
                  target.parentElement!.innerHTML = `<div class="text-2xl font-bold text-primary">${provider.title.charAt(
                    0
                  )}</div>`;
                }}
              />
            </div>
            <div className="text-base font-semibold text-primary">
              {loading === provider.identifier
                ? "Connecting..."
                : provider.title}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};
